import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { ImagePicker } from 'app/design/primitives/image-picker';
import { Input } from 'app/design/primitives/input';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';

import { useForm } from 'app/hooks/form';
import { useNavigation, useRoute } from 'app/hooks/navigation';
import {
  useAcceptBonification,
  useCreateBonification,
  useOneBonification,
  useRejectBonification,
} from 'app/network/bonification';
import { useOneListing } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { borrowerBonificationSchema } from 'app/schemas/bonification';
import type { Listing } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, StyleSheet } from 'react-native';
import { BonificationBadge } from 'app/screens/shared/one-listing/components/bonification-badge';
import { FormError } from 'app/design/primitives/form-error';
import { useOneLoan } from 'app/network/loan';
import { fileTooLargeMessage, isFileTooLargeError } from 'app/lib/upload-errors';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

export const ListingBonificationScreen: NavigationScreen<'listing-bonification'> = () => {
  const { t } = useTranslation('one-channel');
  const route = useRoute<'listing-bonification'>();
  useNavigation({ title: t('bonificationScreen.title') });
  const { loanId, bonificationId, listingId } = route.params;

  const { data: user } = useUser();
  const { data: bonification } = useOneBonification(bonificationId);
  const lid = bonification?.listingId ?? listingId;
  const { data: listing } = useOneListing(lid, 'listing-bonification');
  const loan = useOneLoan(bonification?.loanId ?? loanId).data;

  if (!listing || !user) return null;

  const isBorrower = listing.userId !== user.id;

  if (isBorrower && (!bonification || bonification.status === 'pending')) {
    return (
      <BorrowerForm bonificationId={bonificationId} loanId={loanId} listing={listing} channelUrl={loan?.channelURL} />
    );
  }

  if (!isBorrower && bonification?.status === 'reviewing') {
    const reviewKey = JSON.stringify([
      bonification.id,
      bonification.suggestedTitle,
      bonification.suggestedDescription,
      bonification.suggestedPicture,
      listing.item.name,
      listing.description,
      listing.pictures?.[0]?.url ?? listing.picture,
    ]);
    return <LenderReview key={reviewKey} bonification={bonification} listing={listing} channelUrl={loan?.channelURL} />;
  }

  // Fallback: already reviewed or borrower viewing submitted bonification
  return <BonificationSummary bonification={bonification} listing={listing} />;
};

// -- Borrower form --

interface BorrowerFormProps {
  loanId?: string;
  listing?: Listing;
  bonificationId?: string;
  channelUrl?: string;
}

function BorrowerForm({ loanId, listing, bonificationId, channelUrl }: BorrowerFormProps) {
  const { t } = useTranslation('one-channel');
  const navigation = useNavigation();

  const { control, handleSubmit, watch } = useForm({
    schema: borrowerBonificationSchema,
    defaultValues: {
      title: listing?.item.name ?? '',
      description: listing?.description ?? '',
    },
  });

  const [picture, setPicture] = useState<ImagePickerAsset | null>(
    listing?.pictures?.[0]?.url
      ? ({ uri: listing.pictures[0].url } as ImagePickerAsset)
      : listing?.picture
        ? ({ uri: listing.picture } as ImagePickerAsset)
        : null,
  );
  const [pictureEdited, setPictureEdited] = useState(false);

  const { mutate: create, isPending } = useCreateBonification({
    onSuccess: () => navigation.goBack(),
  });

  const title = watch('title');
  const description = watch('description');

  const onSubmit = handleSubmit(() => {
    const originalTitle = listing?.item.name ?? '';
    const originalDescription = listing?.description ?? '';
    create({
      bonificationId,
      loanId,
      channelUrl,
      suggestedTitle: title !== originalTitle ? title || undefined : undefined,
      suggestedDescription: description !== originalDescription ? description || undefined : undefined,
      picture: pictureEdited ? (picture ?? undefined) : undefined,
    });
  });

  const originalTitle = listing?.item.name ?? '';
  const originalDescription = listing?.description ?? '';
  const noChanges = !pictureEdited && title === originalTitle && description === originalDescription;
  const empty = !pictureEdited && description === '' && title === '';
  const disabled = noChanges || empty || isPending;

  const handleImageChange = (result: ImagePickerSuccessResult) => {
    const asset = result.assets[0];
    if (asset) {
      setPicture(asset);
      setPictureEdited(true);
    }
  };

  return (
    <Page
      accessory={
        <Accessory
          style={{
            paddingHorizontal: theme.spacing['spacing.general.x8'],
            marginTop: theme.spacing['spacing.general.x4'],
          }}
        >
          <Button disabled={disabled} onPress={() => void onSubmit()} variant="primary">
            {t('bonificationScreen.borrower.submit')}
          </Button>
        </Accessory>
      }
    >
      <Column
        gap="spacing.general.x4"
        backgroundColor="surface.warning.subtle"
        borderRadius="radius.lg"
        padding="spacing.general.x4"
        marginBottom="spacing.general.x8"
      >
        <Text variant="bodyBase" fontWeight="600">
          {t('bonificationScreen.borrower.bannerTitle')}
        </Text>
        <Text variant="bodyBase">{t('bonificationScreen.borrower.bannerText')}</Text>
      </Column>

      <Column gap="spacing.general.x8">
        <Column>
          {!picture ? <BonificationBadge /> : null}
          <ImagePicker onChange={handleImageChange} title={t('bonificationScreen.borrower.addPhoto')} value={picture} />
        </Column>

        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }) => (
            <Input
              error={fieldState.error}
              labelSuffix={
                !field.value ? (
                  <Row>
                    <BonificationBadge absolute={false} />
                  </Row>
                ) : undefined
              }
              label={t('bonificationScreen.borrower.titleLabel')}
              onChangeText={field.onChange}
              value={field.value}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }) => (
            <Input
              error={fieldState.error}
              labelSuffix={
                !field.value ? (
                  <Row>
                    <BonificationBadge absolute={false} />
                  </Row>
                ) : undefined
              }
              label={t('bonificationScreen.borrower.descriptionLabel')}
              multiline
              numberOfLines={4}
              onChangeText={field.onChange}
              style={styles.multilineInput}
              value={field.value}
            />
          )}
        />
      </Column>
    </Page>
  );
}

// -- Lender review --

interface LenderReviewProps {
  bonification: {
    id: string;
    suggestedTitle: string | null;
    suggestedDescription: string | null;
    suggestedPicture: string | null;
  };
  listing?: Listing;
  channelUrl?: string;
}

const getLenderReviewInitialValues = (
  bonification: LenderReviewProps['bonification'],
  listing: Listing | undefined,
) => {
  const pictureUrl = bonification.suggestedPicture ?? listing?.pictures?.[0]?.url ?? listing?.picture;
  return {
    title: bonification.suggestedTitle ?? listing?.item.name ?? '',
    description: bonification.suggestedDescription ?? listing?.description ?? '',
    picture: pictureUrl ? ({ uri: pictureUrl } as ImagePickerAsset) : null,
  };
};

const LenderReviewContent = ({
  acceptError,
  bonification,
  description,
  descriptionEdited,
  disabled,
  handleAccept,
  handleImageChange,
  handleReject,
  hasEdits,
  isBusy,
  picture,
  setDescription,
  setTitle,
  t,
  title,
  titleEdited,
}: {
  acceptError: Error | null;
  bonification: LenderReviewProps['bonification'];
  description: string;
  descriptionEdited: boolean;
  disabled: boolean;
  handleAccept: () => void;
  handleImageChange: (result: ImagePickerSuccessResult) => void;
  handleReject: () => void;
  hasEdits: boolean;
  isBusy: boolean;
  picture: ImagePickerAsset | null;
  setDescription: (value: string) => void;
  setTitle: (value: string) => void;
  t: TFunction;
  title: string;
  titleEdited: boolean;
}) => (
  <Page
    accessory={
      <Accessory
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x8'],
          marginTop: theme.spacing['spacing.general.x4'],
        }}
      >
        <Column gap="spacing.general.x2">
          <Button disabled={disabled} onPress={handleAccept} variant="primary">
            <Text color="surface.default">
              {t(
                !bonification.id
                  ? 'bonificationScreen.lender.modify'
                  : hasEdits
                    ? 'bonificationScreen.lender.acceptModified'
                    : 'bonificationScreen.lender.accept',
              )}
            </Text>
          </Button>
          <Button disabled={isBusy} onPress={handleReject} variant="secondary">
            <Text color="text.primary">{t(`bonificationScreen.lender.${bonification.id ? 'reject' : 'cancel'}`)}</Text>
          </Button>
        </Column>
      </Accessory>
    }
  >
    <Column
      gap="spacing.general.x4"
      backgroundColor="surface.warning.subtle"
      borderRadius="radius.lg"
      padding="spacing.general.x4"
      marginBottom="spacing.general.x8"
    >
      <Text variant="bodyBase" fontWeight="600">
        {t('bonificationScreen.lender.bannerTitle')}
      </Text>
      <Text variant="bodyBase">{t('bonificationScreen.lender.bannerText')}</Text>
    </Column>
    <Column gap="spacing.general.x10">
      <ImagePicker onChange={handleImageChange} title={t('bonificationScreen.lender.photoLabel')} value={picture} />
      <Input
        label={t('bonificationScreen.lender.titleLabel')}
        labelRight={!titleEdited && bonification.suggestedTitle ? <SuggestionBadge /> : undefined}
        onChangeText={setTitle}
        value={title}
      />
      <Input
        label={t('bonificationScreen.lender.descriptionLabel')}
        labelRight={!descriptionEdited && bonification.suggestedDescription ? <SuggestionBadge /> : undefined}
        multiline
        numberOfLines={4}
        onChangeText={setDescription}
        style={styles.multilineInput}
        value={description}
      />
    </Column>
    <FormError
      error={
        acceptError
          ? {
              message: isFileTooLargeError(acceptError) ? fileTooLargeMessage(acceptError) : acceptError.message,
              type: 'server',
            }
          : undefined
      }
    />
  </Page>
);

function LenderReview({ bonification, listing, channelUrl }: LenderReviewProps) {
  const { t } = useTranslation('one-channel');
  const navigation = useNavigation();

  const initialValues = getLenderReviewInitialValues(bonification, listing);
  const [title, setTitle] = useState(initialValues.title);
  const [description, setDescription] = useState(initialValues.description);
  const [picture, setPicture] = useState<ImagePickerAsset | null>(initialValues.picture);
  const [pictureEdited, setPictureEdited] = useState(false);

  const {
    mutate: accept,
    isPending: isAccepting,
    error: acceptError,
  } = useAcceptBonification({
    onSuccess: () => navigation.goBack(),
  });
  const { mutate: reject, isPending: isRejecting } = useRejectBonification({
    onSuccess: () => navigation.goBack(),
  });

  const isBusy = isAccepting || isRejecting;

  const titleEdited = title !== (bonification.suggestedTitle ?? listing?.item.name ?? '');
  const descriptionEdited = description !== (bonification.suggestedDescription ?? listing?.description ?? '');
  const hasEdits = titleEdited || descriptionEdited || pictureEdited;

  const disabled = isBusy;

  const handleImageChange = (result: ImagePickerSuccessResult) => {
    const asset = result.assets[0];
    if (asset) {
      setPicture(asset);
      setPictureEdited(true);
    }
  };

  const handleAccept = () => {
    accept({
      id: bonification.id,
      suggestedTitle: title,
      suggestedDescription: description,
      channelUrl: channelUrl,
      ...((pictureEdited || !!bonification.suggestedPicture) && picture ? { picture } : {}),
    });
  };

  const handleReject = () => {
    Alert.alert(t('bonificationScreen.lender.rejectConfirm.title'), t('bonificationScreen.lender.rejectConfirm.text'), [
      { text: t('bonificationScreen.lender.rejectConfirm.cancel'), style: 'cancel' },
      {
        text: t('bonificationScreen.lender.rejectConfirm.confirm'),
        style: 'destructive',
        onPress: () => reject({ id: bonification.id, channelUrl }),
      },
    ]);
  };

  return (
    <LenderReviewContent
      {...{
        acceptError,
        bonification,
        description,
        descriptionEdited,
        disabled,
        handleAccept,
        handleImageChange,
        handleReject,
        hasEdits,
        isBusy,
        picture,
        setDescription,
        setTitle,
        t,
        title,
        titleEdited,
      }}
    />
  );
}

// -- Bonification summary (already reviewed or borrower view of submitted) --

interface BonificationSummaryProps {
  bonification?: {
    status: string;
    suggestedTitle: string | null;
    suggestedDescription: string | null;
    suggestedPicture: string | null;
  };
  listing?: Listing;
}

function BonificationSummary({ bonification, listing }: BonificationSummaryProps) {
  const { t } = useTranslation('one-channel');

  if (!bonification) return null;

  const isAccepted = bonification.status === 'accepted';

  const title = bonification.suggestedTitle ?? listing?.item.name ?? '';
  const description = bonification.suggestedDescription ?? listing?.description ?? '';
  const picture = bonification.suggestedPicture ?? listing?.picture ?? null;

  return (
    <Page>
      <Column
        gap="spacing.general.x4"
        backgroundColor={isAccepted ? 'surface.warning.subtle' : 'surface.primary-neutral'}
        borderRadius="radius.lg"
        padding="spacing.general.x4"
        marginBottom="spacing.general.x8"
      >
        <Text variant="bodyBase" fontWeight="600">
          {t(`bonificationScreen.summary.title.${bonification.status}`)}
        </Text>
        <Text variant="bodyBase">{t(`bonificationScreen.summary.text.${bonification.status}`)}</Text>
      </Column>

      <Column gap="spacing.general.x8">
        {picture ? (
          <Column gap="spacing.general.x1">
            <Text fontWeight="600" fontSize={14}>
              {t('bonificationScreen.lender.photoLabel')}
            </Text>
            <PCImage
              aspectRatio={1}
              height={200}
              source={{ uri: picture }}
              style={{ borderRadius: theme.borderRadii['radius.lg'] }}
            />
          </Column>
        ) : null}

        {title ? (
          <Column gap="spacing.general.x1">
            <Text fontWeight="600" fontSize={14}>
              {t('bonificationScreen.lender.titleLabel')}
            </Text>
            <Text variant="bodyBase">{title}</Text>
          </Column>
        ) : null}

        {description ? (
          <Column gap="spacing.general.x1">
            <Text fontWeight="600" fontSize={14}>
              {t('bonificationScreen.lender.descriptionLabel')}
            </Text>
            <Text variant="bodyBase">{description}</Text>
          </Column>
        ) : null}
      </Column>
    </Page>
  );
}

// -- Suggestion badge --

function SuggestionBadge() {
  const { t } = useTranslation('one-channel');
  return (
    <Row
      style={{
        backgroundColor: theme.colors['surface.warning.subtle'],
        borderRadius: theme.spacing['spacing.general.x1'],
        paddingHorizontal: 8,
        paddingVertical: 2,
      }}
    >
      <Text color="text.placeholder" fontSize={12} fontWeight="700">
        {t(`bonificationScreen.suggestionBadge`)}
      </Text>
    </Row>
  );
}

const styles = StyleSheet.create({
  multilineInput: {
    minHeight: 140,
    textAlignVertical: 'top',
  },
});
